import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, TextField } from "@mui/material";
import JornadaService from "../../services/JornadaService";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import { AccionCorreccion, ICorreccion, TipoFichaje } from "../../types/Jornada";
import { conHora, diaSemanaCorto, HORA_VALIDA, horaFichaje, NOMBRE_FICHAJE } from "../../utils/Jornada";
import * as constantes from "../../common/Constantes";

// Diálogo único de las correcciones del admin: corregir la hora, confirmarla, añadir un fichaje o anularlo, siempre con
// motivo. La API valida la secuencia del día y devuelve el mensaje si no encaja.
export interface PeticionCorreccion {
    accion: AccionCorreccion;
    especialistaId: number;
    nombre: string;
    fecha: string;                                            // 'YYYY-MM-DD' del día que se corrige
    evento?: { _id: string; tipo: TipoFichaje; hora: string }; // corregir, confirmar y anular
    tipo?: TipoFichaje;                                       // añadir: el tipo propuesto
    hora?: string;                                            // 'HH:mm' propuesta
}

interface CorreccionDialogProps {
    peticion: PeticionCorreccion | null;
    onClose: () => void;
    onGuardado: (mensaje: string) => void;
    pausas: boolean; // si la clínica ficha las pausas: sin ellas, solo se añaden entradas y salidas
}

type Fallo = { message?: string; response?: { status?: number; data?: { message?: string } } };

const TITULO: Record<AccionCorreccion, string> = { corregir: 'Corregir fichaje', confirmar: 'Confirmar hora', anadir: 'Añadir fichaje', anular: 'Anular fichaje' };
const HECHO: Record<AccionCorreccion, string> = { corregir: 'Fichaje corregido', confirmar: 'Hora confirmada', anadir: 'Fichaje añadido', anular: 'Fichaje anulado' };
const MOTIVO_CONFIRMAR = 'Hora confirmada por el administrador';

const CorreccionDialog = ({ peticion, onClose, onGuardado, pausas }: CorreccionDialogProps) => {
    const history = useNavigate();
    const [tipo, setTipo] = useState<TipoFichaje>('entrada');
    const [hora, setHora] = useState('');
    const [motivo, setMotivo] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!peticion) return;
        setTipo(peticion.evento?.tipo ?? peticion.tipo ?? 'entrada');
        setHora(peticion.hora ?? '');
        setMotivo(peticion.accion === 'confirmar' ? MOTIVO_CONFIRMAR : '');
        setError('');
        setEnviando(false);
    }, [peticion]);

    const accion = peticion?.accion;
    const conHoraNueva = accion === 'corregir' || accion === 'anadir';

    const guardar = () => {
        if (!peticion || !accion) return;
        let horaIso: string | undefined;
        if (conHoraNueva) {
            if (!HORA_VALIDA.test(hora)) { setError('Indica una hora válida'); return; }
            const elegida = conHora(peticion.fecha, hora);
            if (elegida.getTime() > Date.now()) { setError('No puedes poner una hora futura'); return; }
            horaIso = elegida.toISOString();
        }
        if (!motivo.trim()) { setError('Indica el motivo'); return; }

        const correccion: ICorreccion = {
            accion,
            especialistaId: peticion.especialistaId,
            ...(peticion.evento ? { eventoId: peticion.evento._id } : {}),
            ...(accion === 'anadir' ? { tipo } : {}),
            ...(horaIso ? { hora: horaIso } : {}),
            motivo: motivo.trim(),
        };
        setEnviando(true);
        setError('');
        JornadaService.corregir(correccion)
            .then(() => onGuardado(HECHO[accion]))
            .catch((e: unknown) => {
                const fallo = e as Fallo;
                if (fallo.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                    return;
                }
                if (![400, 403, 404].includes(fallo.response?.status ?? 0)) {
                    const log: ILog = { method: 'CorreccionDialog.corregir', level: 'error', message: fallo.message ?? '', meta: fallo.response };
                    LogService.logError(log);
                }
                // El diálogo sigue abierto con el mensaje (secuencia que no encaja, sin conexión…)
                setError(fallo.response?.data?.message || 'No se ha podido guardar, vuelve a intentarlo');
            })
            .finally(() => setEnviando(false));
    };

    return (
        <Dialog open={peticion !== null} maxWidth="xs" fullWidth onClose={() => !enviando && onClose()}>
            <DialogTitle>{accion ? TITULO[accion] : ''}</DialogTitle>
            <DialogContent>
                {peticion && (
                    <>
                        <DialogContentText sx={{ mb: 2 }}>
                            {peticion.nombre} · {diaSemanaCorto(peticion.fecha, true)}
                            {peticion.evento && <><br />{NOMBRE_FICHAJE[peticion.evento.tipo]} a las {horaFichaje(peticion.evento.hora)}</>}
                        </DialogContentText>
                        {accion === 'anadir' && (
                            <TextField select size="small" label="Fichaje" value={tipo} fullWidth sx={{ mb: 2 }} onChange={(e) => setTipo(e.target.value as TipoFichaje)}>
                                {(Object.keys(NOMBRE_FICHAJE) as TipoFichaje[]).filter(t => pausas || (t !== 'inicioPausa' && t !== 'finPausa')).map(t => <MenuItem key={t} value={t}>{NOMBRE_FICHAJE[t]}</MenuItem>)}
                            </TextField>
                        )}
                        {conHoraNueva && (
                            <TextField type="time" size="small" label={accion === 'corregir' ? 'Hora correcta' : 'Hora'} value={hora} InputLabelProps={{ shrink: true }} inputProps={{ step: 60 }}
                                sx={{ mb: 2 }} onChange={(e) => setHora(e.target.value)} />
                        )}
                        <TextField label="Motivo" value={motivo} fullWidth multiline minRows={2} required inputProps={{ maxLength: 300 }}
                            helperText="Queda guardado junto al fichaje original" onChange={(e) => setMotivo(e.target.value)} />
                    </>
                )}
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                {enviando ? <CircularProgress size={24} sx={{ mr: 2 }} /> : (
                    <>
                        <Button onClick={guardar} variant="contained">{accion === 'anular' ? 'ANULAR' : accion === 'confirmar' ? 'CONFIRMAR' : 'GUARDAR'}</Button>
                        <Button onClick={onClose}>CANCELAR</Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CorreccionDialog;
